import React from "react";
import cn from "classnames";
import styles from "./drag-image.module.scss";
import { useI18N } from "@src/language/language-context";

interface DragImagePropTypes {
  count: number;
  text: string;
  icon: string;
}

const checkBoxIcon =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABkAAAAZCAYAAADE6YVjAAABa0lEQVR4Ae2UvU8CMRjGnytHFFyIH5Maz8HFCQcTPhZv05FITPwjHFx0c3DTxDi4upsQEkbdCAMBZZA/ocYw+REc0JC7Xu0x+AHXa2GF39Le26fvc32bt8CEscTQEaVKNQsurP444167sZdtqvaHmmwWqkmTRC6FaEum4QDlDrfr+2k6tEnqpmaRqPEopgmoaTses2WnIrJdwqCsaeCT8E8szRUUzBQekmKwoKDbevqZ+yXt3Z2uiWcw5Qne7oqg58f/jEwX+iY6Bq+3Rcxt5zG1uKLUK02c9xe0ri96Y7/B/E4eOpgqAYnFRUkonq9OEV9bx8d9ZSiDXg6VIBKbwfLBCdhXZyQDH1MSpWC/n9HZBVhHZ2CfHUwvWZDRFS9AUFzajOlivRzW6f1wA7S2m1oNWpOWy42wQ/ELbeggdJxwW7YsNWnksk2P8A2RgCIMzisOYXY9N8Lb9ZdMqZokLhloUFfcXVjyCWPON6lSfODnsmpxAAAAAElFTkSuQmCC";

const folderIcon =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAYAAADgdz34AAABPElEQVR4Ae2VwU7CQBCGZ5bSXmwjkiZIPHjqC3DzxAPoyROv5gt4kmfwpA8gp548GEEbEuiWkFI74y6hHEwpS+RC0v/Q2SZ/9pvppv8C1NojLBZz5oullDdE0C0zMgopKHu9bLXe4VDAN/NZLhf3yLAi/gnLjAKtgJhcEvB85XmhKcDSD0qSayZ2rCwddnxflhmZ+W08T+4EcX8ipd1x3ZEJYD3B52zWUz32uufeQ5VZQWwNUav2bhdNSQg95XQ7gakQcaXKUxTHQUbklnn0p1Rd36pmHrX/IEAhv+IMoigKs6Yz+EqSQL2OBBxZ/uYMKc8dXY8O+KsaUANOBSAajVRXnarwTxV7FHuuw06H2EQuBqTiGnfEtalYZVETwcZ0OdR/9fbC+YjjtiDsVyelAYBg7Frw4m3StNZe/QJmc4S2BVDIggAAAABJRU5ErkJggg==";

const nodeIcon =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAYAAADgdz34AAAAvElEQVR4AeWUUQ6DIAyGy+K7B9hlvJl41mXbI2E7QLWJJI0ptcX4xJ8QsNYfGr8C0JVeKU004A69c54/+Yc0aG39brAkkWGAEBFwoWdabzF4juMCV1VOvs2RxaK1kgecK+wzshge3l0TP7FUUYvhfCy/bCKZS/mqeY0WMpbMzXTx8i0/0pVPDeShRaOLN6NEkZWWKl1Drb28tDTR5aVFy1c38dAi5bvkpavZnMV6v4u0TW4xL/qm/0QDutIKa96ZeAWJsPIAAAAASUVORK5CYII=";

const checkboxImage = new Image();
checkboxImage.src = checkBoxIcon; // The path to your node icon

// Preload images when the component or application is initialized
const folderImage = new Image();
folderImage.src = folderIcon; // The path to your folder icon

const nodeImage = new Image();
nodeImage.src = nodeIcon; // The path to your node icon

export const DragImage = ({ count, text, icon }: DragImagePropTypes) => {
  const i18n = useI18N();
  const texts = {
    checkBoxIconAltText: i18n("modules.dragImage.checkBoxIconAltText"),
    dragImageAltText: i18n("modules.dragImage.dragImageAltText"),
  };
  const renderIcon = () => {
    return icon === "FOLDER" ? folderIcon : nodeIcon;
  };

  return (
    <>
      {folderImage.complete && nodeImage.complete && checkboxImage.complete && (
        <div className={styles["drag-image"]}>
          <div
            className={cn(styles["drag-image__main-wrapper"], {
              [styles["drag-image__main-wrapper--tilted-right"]]: count > 1,
            })}
          >
            <div className={styles["drag-image__left-wrapper"]}>
              <img
                className={styles["drag-image__checkbox-icon"]}
                src={checkboxImage.src}
                alt={texts.checkBoxIconAltText}
              />
              <img
                src={renderIcon()}
                className={styles["drag-image__dynamic-icon"]}
                alt={texts.dragImageAltText}
              />
              <p>{text}</p>
            </div>
            <div className={styles["drag-image__count"]}>{count}</div>
          </div>
          {count > 1 && (
            <div
              className={cn(
                styles["drag-image__main-wrapper"],
                styles["drag-image__main-wrapper--tilted-left"],
              )}
            />
          )}
        </div>
      )}
    </>
  );
};
